import { getMappedLanguage } from 'i18n/helpers';
import { SATISFACTION_RESPONSE_ENDPOINT } from './constants';
import {
  SatisfactionResponse,
  SatisfactionForm,
  SatisfactionCallbackResponse,
  UpdateSatisfactionResponseOptions,
} from './types';

export const validateUpdateSatisfactionResponseParams = (
  satisfactionResponseId,
  parameters: UpdateSatisfactionResponseOptions,
) => {
  if (!satisfactionResponseId) {
    return new Error('A satisfactionId must be provided.');
  }

  const { rating, answers } = parameters;

  if (answers) {
    const exceededLengthAnswer = answers.find((answer) => {
      return answer.answer?.length > 1024;
    });

    if (exceededLengthAnswer) {
      return new Error('Answers cannot exceed 1024 characters');
    }
  }

  if (rating && (typeof rating !== 'number' || rating > 10 || rating < 1)) {
    return new Error('Rating must be a number between 1 and 10');
  }

  return undefined;
};

export const createSatisfactionCallbackResponse = (
  response: SatisfactionResponse,
  pubNubTimetoken?: string | number,
  form?: SatisfactionForm,
): SatisfactionCallbackResponse => {
  const callbackResponse = {} as SatisfactionCallbackResponse;

  const conversationId = response?.relationships?.conversation?.data?.id;
  const sessionId = response?.relationships?.session?.data?.id;

  if (!response || !form || (!conversationId && !sessionId))
    return callbackResponse;

  const { id } = response;
  const { rating, answers, createdAt, lockedAt } = response.attributes;

  callbackResponse.conversationId = conversationId || sessionId;
  callbackResponse.satisfactionId = id;

  let timetoken = createdAt;

  if (pubNubTimetoken) {
    const convertedToken = Number(pubNubTimetoken) / 10000;
    timetoken = new Date(convertedToken).toISOString();
  }

  callbackResponse.timetoken = timetoken;
  callbackResponse.lockedAt = lockedAt;

  callbackResponse.response = {
    rating,
    answers,
  };

  if (form) {
    const { description, introduction, name, questions, ratingPrompt, scale } =
      form.attributes;

    callbackResponse.form = {
      description,
      introduction,
      name,
      questions,
      ratingPrompt,
      scale,
    };
  }

  return callbackResponse;
};

export const makeSatisfactionResponseEndpoint = (id: string, lang?: string) => {
  const mappedLang = getMappedLanguage(lang);

  let endpoint = `/c/v3/chat/satisfaction-responses/${id}?include=satisfaction`;

  if (mappedLang) {
    endpoint += `&lang=${mappedLang}`;
  }

  return endpoint as typeof SATISFACTION_RESPONSE_ENDPOINT;
};

export const makeTranslatedSatisfactionFormEndpoint = (
  satisfactionId: string,
  lang?: string,
) => {
  const mappedLang = getMappedLanguage(lang);
  let endpoint = `/c/v3/chat/satisfaction/${satisfactionId}/translated-form`;

  if (mappedLang) {
    endpoint += `?lang=${mappedLang}`;
  }

  return endpoint;
};
